import {
  Brand,
  BrandFamily,
  Domains,
  FlightProblems,
  FlowType,
  Languages,
  Locales,
  TargetingData,
  TranslationTenant,
} from "@/core/types/types";
import { getBasicConfig } from "@/core/utils/";
import { getPartnerPromotionCode, getTargetingCookies } from "@/utils/";
import {
  CaseDataToken,
  deserializeCaseDataToken,
} from "@/APR/utils/base64Utils";
import { hasProcessCookie } from "./hasProcessCookie";
import { ParsedUrlQuery } from "querystring";
import { IncomingHttpHeaders } from "http";

export enum LegalArea {
  DEFAULT = "apr",
  APR = "apr",
  MIE = "mie",
  ARB = "arb",
  VER = "ver",
}

export interface RequiredBasicConfigProps {
  domain: Domains;
  language: Languages;
  locale: Locales;
  definitionName: FlowType;
  brandFamily: BrandFamily;
  brandId: Brand;
  legalArea: LegalArea;
  translationTenant: TranslationTenant;
}

interface BasicInitialisationConfig extends RequiredBasicConfigProps {
  promotionCode?: string | null;
  processId?: string;
  action?: string;
  utm_campaign?: string;
  initialURL?: string | null;
  forceShowBackupCC?: boolean | false;
  legalArea: LegalArea;
  notFoundProcess?: boolean | false;
  fullRequestPageUrl?: string | string[];
}

interface MIEInitialisationConfig extends BasicInitialisationConfig {
  // MIE-specific config options
  isNewInstance?: boolean;
}

interface FlightrightInitialisationConfig extends BasicInitialisationConfig {
  query?: string;
}

interface NordicPartnerInitialisationConfig extends BasicInitialisationConfig {
  partnerClickId?: string | null;
}

export interface APRInitialisationConfig
  extends NordicPartnerInitialisationConfig,
    FlightrightInitialisationConfig {
  flightProblem?: FlightProblems;
  partnerToken?: string | null;
  isNewInstance?: boolean;
  syncSignature?: boolean;
}

export type InitialisationConfig =
  | APRInitialisationConfig
  | MIEInitialisationConfig;

/** getInitialisationConfig is  a  utility that helps us to define which partner and branding
 * to use in our application  with  the help  of url hostname.
 * The signature of the urls is based on this  schema:
 * https://allright-camunda-app.[staging/develop?].[BRAND_DOMAIN_NAME].tld
 * *** [staging/develop] are optional parts
 * *** [BRAND_DOMAIN_NAME] = [gofreem], [chevalier], [flightright], [flugrecht], [flyhjaelp], [allright]
 *
 * Possible urls that may occur are following:
 * - allright-camunda-app.flightright.de
 * - allright-camunda-app.develop.flightright.de
 * - allright-camunda-app.gofreem.de
 * - allright-camunda-app.advocard.gofreem.de
 * - allright-camunda-app.advocard.develop.flyhjaelp.net
 * etc.
 *
 * @param {String} host - host is hostname of global location object, that serves
 * for defining the partner and branding names
 *
 * @param {Object} query - the query string from the URL, e.g. for when a promotionCode needs to be applied
 *
 * @returns {BrandDetails} - Returns an object with brand name and brand family, that will help to
 * identify styling theme
 */

interface RequestConfig {
  host?: string;
  cookies?: {};
  query?: ParsedUrlQuery;
  headers?: IncomingHttpHeaders;
  path?: string;
  urlWithUtmSource?: string | null;
}
export default function getInitialisationConfig({
  host = "",
  cookies = {},
  query,
  headers = {},
  path = "",
  urlWithUtmSource,
}: RequestConfig): InitialisationConfig {
  const config: RequiredBasicConfigProps = getBasicConfig({
    host,
    headers,
    path,
    query,
  });

  return getGeneralConfig(config, cookies, query, urlWithUtmSource);
}

function getGeneralConfig(
  baseConfig: RequiredBasicConfigProps,
  cookies: {},
  query?: any,
  urlWithUtmSource?: string | null
): InitialisationConfig {
  const promotionCode: string | undefined = getPartnerPromotionCode(
    cookies,
    query
  );
  const {
    processinstanceid: processId,
    action,
    definitionName,
    flightProblem,
    caseDataToken,
    partnerToken = null,
    utm_source = null,
    utm_campaign,
    syncSignature,
  } = query || {};

  const targetingData: TargetingData | null = getTargetingCookies(
    cookies,
    utm_source ? urlWithUtmSource : null
  );

  const caseData: CaseDataToken | null =
    deserializeCaseDataToken(caseDataToken);

  const processIdCookie: boolean = hasProcessCookie(cookies);

  return {
    ...baseConfig,
    ...caseData,
    ...targetingData,
    partnerToken: !processIdCookie ? partnerToken : null,
    definitionName: definitionName ?? baseConfig.definitionName,
    flightProblem: flightProblem ?? null,
    promotionCode: promotionCode ?? null,
    processId: processId ?? null,
    action: action ?? null,
    utm_campaign: utm_campaign ?? null,
    query: query ?? null,
    syncSignature: syncSignature ?? null,
  };
}
